import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraFormField, SpecraOverlayDialog, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PursuitTargetCombobox } from './PursuitTargetCombobox';
import { ReviewAck, SurgeryError, SurgeryFooterActions } from './SurgeryDialogParts';
import { useMergePursuits, usePursuitSurgeryPlan } from './surgeryHooks';
/**
 * GRW-19: "Merge into another pursuit…". This pursuit becomes a read-only
 * stub pointing at the target; movable components move, decision history
 * stays on the stub. The target can never be this pursuit or a merged one.
 */ export function MergePursuitDialog({ captureProjectId, pursuitName, open, onOpenChange }) {
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: "Merge into another pursuit",
        size: "md",
        testId: Testids.pursuitSurgery.mergeDialog,
        children: open && /*#__PURE__*/ _jsx(MergeBody, {
            captureProjectId: captureProjectId,
            pursuitName: pursuitName,
            onDone: ()=>onOpenChange(false)
        })
    });
}
function MergeBody({ captureProjectId, pursuitName, onDone }) {
    const navigate = useNavigate();
    const [requestId] = useState(()=>crypto.randomUUID());
    const plan = usePursuitSurgeryPlan(captureProjectId, true);
    const merge = useMergePursuits();
    const [target, setTarget] = useState({
        id: '',
        name: ''
    });
    const [reason, setReason] = useState('');
    const [ack, setAck] = useState(false);
    // A merge moves every movable component, RFI links and proposal links included.
    const needsReview = (plan.data?.reviewRequiredIfMovingRfis ?? false) || (plan.data?.components ?? []).some((c)=>c.kind === 'proposal_link' && c.movable);
    const canSubmit = !!target.id && target.id !== captureProjectId && !!reason.trim() && (!needsReview || ack) && !merge.isPending;
    const submit = ()=>merge.mutate({
            loserCaptureProjectId: captureProjectId,
            survivorCaptureProjectId: target.id,
            reason: reason.trim(),
            requestId,
            proposalReviewAck: ack
        }, {
            onSuccess: ()=>{
                onDone();
                navigate(`/capture/${target.id}`);
            }
        });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Merge into",
                required: true,
                children: /*#__PURE__*/ _jsx(PursuitTargetCombobox, {
                    excludeId: captureProjectId,
                    value: target.id,
                    onChange: (id, name)=>setTarget({
                            id,
                            name
                        })
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                size: "body-ui",
                tone: "secondary",
                children: [
                    pursuitName,
                    " becomes a read-only stub that points to ",
                    target.name || 'the other pursuit',
                    ". Touchpoints, RFI responses, tasks, documents and teaming move. Decisions and black-hat history stay on the stub."
                ]
            }),
            needsReview && /*#__PURE__*/ _jsx(ReviewAck, {
                proposals: plan.data?.impactedProposals ?? [],
                verb: "merge",
                checked: ack,
                onCheckedChange: setAck
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Reason",
                htmlFor: "merge-reason",
                required: true,
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    id: "merge-reason",
                    value: reason,
                    onChange: (e)=>setReason(e.target.value),
                    "data-testid": Testids.pursuitSurgery.reasonInput
                })
            }),
            /*#__PURE__*/ _jsx(SurgeryError, {
                error: merge.error
            }),
            /*#__PURE__*/ _jsx(SurgeryFooterActions, {
                onCancel: onDone,
                onSubmit: submit,
                disabled: !canSubmit,
                pending: merge.isPending,
                label: "Merge"
            })
        ]
    });
}
